import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  CustomerPrintSettings,
  PrintCopyType,
  PrintSet,
} from "../../../types/customer.type";
import { updateCustomerPrintSettings } from "../../../services";
import { COPY_TYPES } from "./printCopyTypes";
import EtaxPrintSetView from "./EtaxPrintSetView";

interface PrintSettingsModalProps {
  open: boolean;
  customerUuid: string;
  customerName: string;
  customerCode: string;
  settings: CustomerPrintSettings | null;
  // The customer's `etax_enabled`: its print set is fixed, so it is shown, never edited.
  isEtaxShop: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const MIN_QTY = 1;
const MAX_QTY = 9;

// Local editing shape: quantities keyed by copy type. A key present means the
// copy type is selected. Converted to the API's `copies` array on save.
interface DraftSet {
  id: string;
  name: string;
  sel: Partial<Record<PrintCopyType, number>>;
}

let draftSeq = 0;
const nextId = () => `draft-${Date.now()}-${draftSeq++}`;

const toDraft = (sets: PrintSet[]): DraftSet[] =>
  sets.map((set) => ({
    id: nextId(),
    name: set.name,
    sel: set.copies.reduce<Partial<Record<PrintCopyType, number>>>(
      (acc, copy) => {
        acc[copy.type] = copy.quantity;
        return acc;
      },
      {},
    ),
  }));

const setSize = (sel: DraftSet["sel"]) =>
  COPY_TYPES.reduce((total, t) => total + (sel[t.key] ?? 0), 0);

// Draft → API body. Emits copies in fixed A→D order for the selected types.
const toSets = (draft: DraftSet[]): PrintSet[] =>
  draft.map((d) => ({
    name: d.name.trim(),
    copies: COPY_TYPES.filter((t) => d.sel[t.key] != null).map((t) => ({
      type: t.key,
      quantity: d.sel[t.key] as number,
    })),
  }));

export default function PrintSettingsModal({
  open,
  customerUuid,
  customerName,
  customerCode,
  settings,
  isEtaxShop,
  onClose,
  onSaved,
}: PrintSettingsModalProps) {
  const [draft, setDraft] = useState<DraftSet[]>([]);
  const [loading, setLoading] = useState(false);

  // Seed the draft from the loaded settings each time the modal opens.
  useEffect(() => {
    if (open) {
      setDraft(toDraft(settings?.sets ?? []));
    }
  }, [open, settings]);

  const updateSet = (id: string, patch: (d: DraftSet) => DraftSet) =>
    setDraft((prev) => prev.map((d) => (d.id === id ? patch(d) : d)));

  const handleName = (id: string, name: string) =>
    updateSet(id, (d) => ({ ...d, name }));

  const handleToggle = (id: string, key: PrintCopyType) =>
    updateSet(id, (d) => {
      const sel = { ...d.sel };
      if (sel[key] != null) delete sel[key];
      else sel[key] = MIN_QTY;
      return { ...d, sel };
    });

  const handleBump = (id: string, key: PrintCopyType, delta: number) =>
    updateSet(id, (d) => {
      const cur = d.sel[key] ?? 0;
      const next = Math.max(MIN_QTY, Math.min(MAX_QTY, cur + delta));
      return { ...d, sel: { ...d.sel, [key]: next } };
    });

  const handleAddSet = () =>
    setDraft((prev) => [
      ...prev,
      { id: nextId(), name: "", sel: { A: MIN_QTY } },
    ]);

  const handleRemoveSet = (id: string) =>
    setDraft((prev) => prev.filter((d) => d.id !== id));

  // Mirrors the server rules: ≥1 set, each set has a non-empty name + ≥1 copy,
  // every quantity within 1..9 (the stepper already clamps).
  const canSave = useMemo(() => {
    if (draft.length === 0) return false;
    return draft.every((d) => {
      if (d.name.trim().length === 0) return false;
      const keys = COPY_TYPES.filter((t) => d.sel[t.key] != null);
      if (keys.length === 0) return false;
      return keys.every((t) => {
        const q = d.sel[t.key] as number;
        return q >= MIN_QTY && q <= MAX_QTY;
      });
    });
  }, [draft]);

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    try {
      await updateCustomerPrintSettings(customerUuid, { sets: toSets(draft) });
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        ตั้งค่าการพิมพ์
        <Typography variant="caption" color="text.secondary" display="block">
          {customerName} · {customerCode}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isEtaxShop ? (
          <EtaxPrintSetView />
        ) : (
          <Stack gap={2}>
            {draft.map((d, index) => {
              const canRemove = draft.length > 1;
              return (
                <Box
                  key={d.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {/* Set header: index badge + name + total + remove */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      bgcolor: "#f7f7fb",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "8px",
                        bgcolor: "primary.main",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <TextField
                      value={d.name}
                      onChange={(e) => handleName(d.id, e.target.value)}
                      placeholder="ชื่อชุด เช่น ใบเต็มชุด"
                      size="small"
                      fullWidth
                      error={d.name.trim().length === 0}
                    />
                    <Chip
                      label={`${setSize(d.sel)} ใบ`}
                      size="small"
                      sx={{
                        bgcolor: "#FFF5E7",
                        color: "#8A5A00",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    />
                    <IconButton
                      aria-label="ลบชุด"
                      color="error"
                      size="small"
                      disabled={!canRemove}
                      onClick={() => handleRemoveSet(d.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Copy-type rows */}
                  <Box sx={{ px: 2, py: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ width: 42, flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ flex: 1 }}
                      >
                        ประเภทบิลในชุด
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ width: 132, textAlign: "center" }}
                      >
                        จำนวน (ใบ)
                      </Typography>
                    </Box>

                    {COPY_TYPES.map((t) => {
                      const checked = d.sel[t.key] != null;
                      const qty = d.sel[t.key] ?? 0;
                      return (
                        <Box
                          key={t.key}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 0.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => handleToggle(d.id, t.key)}
                            size="small"
                            sx={{ p: 0, width: 42, flexShrink: 0 }}
                          />
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "baseline",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              {t.key}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t.label}
                            </Typography>
                          </Box>
                          {checked ? (
                            <Box
                              sx={{
                                width: 132,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <IconButton
                                aria-label="ลด"
                                size="small"
                                disabled={qty <= MIN_QTY}
                                onClick={() => handleBump(d.id, t.key, -1)}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: "8px 0 0 8px",
                                  color: "primary.main",
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Box
                                sx={{
                                  minWidth: 44,
                                  height: 34,
                                  px: 1,
                                  borderTop: "1px solid",
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {qty}
                              </Box>
                              <IconButton
                                aria-label="เพิ่ม"
                                size="small"
                                disabled={qty >= MAX_QTY}
                                onClick={() => handleBump(d.id, t.key, 1)}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: "0 8px 8px 0",
                                  color: "primary.main",
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ width: 132, textAlign: "center" }}
                            >
                              ยังไม่เลือก
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}

            <Button
              onClick={handleAddSet}
              startIcon={<AddIcon />}
              fullWidth
              sx={{
                border: "1.5px dashed",
                borderColor: "primary.light",
                py: 1.25,
              }}
            >
              เพิ่มชุด
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                color: "text.secondary",
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 16, mt: "2px", flexShrink: 0 }}
              />
              <Typography variant="caption">
                แต่ละชุดกำหนดได้ว่าจะพิมพ์บิลประเภทใดบ้าง (A/B สำเนา · C/D ต้นฉบับ)
                และแต่ละประเภทกี่ใบ · ต้องเลือกอย่างน้อย 1 ประเภทต่อชุด
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {isEtaxShop ? "ปิด" : "ยกเลิก"}
        </Button>
        {!isEtaxShop && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
            loading={loading}
          >
            บันทึกการตั้งค่า
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
